package com.bikompanion.data.rating.firebase

import dev.gitlive.firebase.auth.FirebaseAuth
import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import com.bikompanion.domain.AnonymousIdentityProvider
import com.bikompanion.domain.di.AppScope

/**
 * The rider's identity, as a Firebase anonymous account.
 *
 * This is what makes the security rules mean anything: `auth.uid` is the only thing a rule can
 * verify, and the self-generated hex this replaced was client-invented — a rider could hand the
 * backend any uid they liked, which made the vote cooldown unenforceable.
 *
 * A singleton, and not incidentally: two concurrent first calls would otherwise each run their
 * own anonymous sign-up and the app would quietly accumulate identities, one of which owns the
 * cooldown markers and the other of which doesn't.
 */
@ContributesBinding(AppScope::class)
@Inject
@SingleIn(AppScope::class)
class AnonymousIdentityProviderImpl(
    private val auth: FirebaseAuth,
) : AnonymousIdentityProvider {
    private val signInMutex = Mutex()

    override suspend fun currentId(): String =
        signInMutex.withLock {
            auth.currentUser?.uid
                ?: requireNotNull(auth.signInAnonymously().user) { "Anonymous sign-in returned no user" }.uid
        }
}
